import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useIsPortraitMobile } from "../../../hooks/useIsPortraiteMobile";
import TiltMessage from "../../../components/feedback/TiltMessage";
import { SectionHeader } from "../../SectionHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { EcommSalesCard } from "../components/ecomm-salesCard";
import { EcommSalesByTime } from "../components/ecomm-salesByTime";
import { EcommCategoryPerformance } from "../components/ecomm-categoryPerformance";
import { EcommTopProducts } from "../components/ecomm-topSelling";
import { EcommSalesDataTable } from "../components/ecomm-salesDataTable";
import { MonthYearPicker } from "../../ui/month-Year-Picker";
import { TriangleAlert } from "lucide-react";


export function EcommerceSales() {
    const now = new Date();
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date(now.getFullYear(), now.getMonth()));
    const formattedDate = selectedDate ? format(selectedDate, "yyyy-MM") : "";

    const isCurrentMonthIncomplete = useMemo(() => {
            if (!selectedDate) return false;
            const selectedMonthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
            const selectedMonthEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
            return now >= selectedMonthStart && now <= selectedMonthEnd && now.getDate() < selectedMonthEnd.getDate();
        }, [selectedDate, now]);

    const showTiltMessage = useIsPortraitMobile();

    return (
        <div className="space-y-8">
            <TiltMessage show={showTiltMessage} />
            <div className={showTiltMessage ? "blur-sm pointer-events-none" : ""}>
                <SectionHeader title="Sales and Product Reports" />
                <Tabs defaultValue="overview">
                    {/* Month-Year Picker for selectedMonth */}
                    <div className="flex flex-row items-center justify-between w-full">
                    <MonthYearPicker className="mb-2" selected={selectedDate} onChange={setSelectedDate} />
                    {isCurrentMonthIncomplete && (
                        <div className="text-sm text-yellow-200 bg-white/70 dark:bg-black/70 rounded-xl shadow-xl-soft px-3 py-2 flex flex-row items-center">
                            <TriangleAlert /> The current month is incomplete.
                        </div>
                    )}
                    </div>
                    
                    <TabsList className="m-0">
                        <TabsTrigger value="overview">Overview</TabsTrigger>
                        <TabsTrigger value="products">Products</TabsTrigger>
                        <TabsTrigger value="transactions">Transactions</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent
                        value="overview"
                        className="grid grid-cols-6 gap-x-8 w-full h-full p-0 bg-transparent dark:bg-transparent rounded-none shadow-none-soft overflow-visible"
                    >
                        {/* Pass selectedMonth to EcommSalesCard */}
                        <div className="col-span-6 h-full flex flex-col">
                            <EcommSalesCard selectedMonth={formattedDate} />
                        </div>
                        
                        {/* Pass selectedMonth to EcommSalesByTime */}
                        <div className="col-span-6 h-full flex flex-col">
                            <EcommSalesByTime />
                        </div>
                        
                        {/* Pass selectedMonth to EcommCategoryPerformance */}
                        <div className="col-span-6 h-full flex flex-col">
                            <EcommCategoryPerformance selectedMonth={formattedDate} />
                        </div>
                    </TabsContent>
                    
                    <TabsContent
                        value="products"
                        className="grid grid-cols-6 gap-x-8 w-full h-full p-0 bg-transparent dark:bg-transparent rounded-none shadow-none-soft overflow-visible"
                    >
                        <div className="col-span-6 h-full flex flex-col">
                            <EcommTopProducts selectedMonth={formattedDate} />
                        </div>
                    </TabsContent>
                    
                    <TabsContent
                        value="transactions"
                        className="grid grid-cols-6 gap-x-8 w-full h-full p-0 bg-transparent dark:bg-transparent rounded-none shadow-none-soft overflow-visible"
                    >
                        <div className="col-span-6 h-full flex flex-col">
                            <EcommSalesDataTable />
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}